import { TStats, TSparseItem } from '@modules/items';
import { TPermissions } from '@modules/access';
import { TBulkOperation } from '@modules/items/types/bulkOperation.types';

export enum ELiteratureType {
  CLINICAL_TRIAL = 'clinical-trial',
  META_ANALYSIS = 'meta-analysis',
  RCT = 'rct',
  REVIEW = 'review',
  SYSTEMATIC_REVIEW = 'systematic-review',
  BOOK_OR_DOCUMENT = 'book-or-document',
  OTHER = 'other',
}

export enum EAssessmentDecision {
  INCLUDED = 'included',
  EXCLUDED = 'excluded',
  PENDING = 'pending',
  // Response-only - a document without a product has nothing to assess against. Never stored; not in the backend enum.
  // WARNING Mirrored in FE/BE - keep in sync. Counterpart: src/documents/types/documents.types.ts
  NOT_REQUIRED = 'not-required',
}

export enum EReviewingStage {
  NOT_REVIEWED = 'not-reviewed',
  ABSTRACT_APPROVED = 'abstract-approved',
  FULL_TEXT_APPROVED = 'full-text-approved',
}

export enum EExclusionReason {
  WRONG_DEVICE = 'wrong-device',
  WRONG_INDICATION = 'wrong-indication',
  WRONG_POPULATION = 'wrong-population',
  WRONG_OUTCOME = 'wrong-outcome',
  WRONG_STUDY_DESIGN = 'wrong-study-design',
  WRONG_PUBLICATION_TYPE = 'wrong-publication-type',
  PRECLINICAL = 'preclinical',
  DUPLICATE = 'duplicate',
  OTHER = 'other',
}

export type TLiteratureContent = {
  type: ELiteratureType;
  title: string;
  authors?: string[];
  publicationYear?: number;
  volume?: string;
  issue?: string;
  pages?: string;
  containerTitle?: string;
  publisher?: string;
  doi?: string;
  url?: string;
  accessDate?: string;
  pmid?: string;
  attachmentBlobName?: string;
};

export type TLiteratureDraft = {
  content: TLiteratureContent;
  createdDate: string;
};

export type TLiteratureVersion = {
  id: string;
  content: TLiteratureContent;
  createdDate: string;
  completedDate: string;
  authors: string[];
  note: string;
};

export type TLiteratureAssessment = {
  _id: string;
  products: string[];
  decision: EAssessmentDecision;
  reviewingStage?: EReviewingStage;
  exclusionReason?: EExclusionReason;
  comment?: string;
  reviewer: string;
  reviewDate: string;
  archived: boolean;
};

// Input shape for create-assessment endpoints - used by `useCreateAssessment`, by the bulk-upload payload,
// and by the product-scoped inline assessment form.
export type TAssessmentInput = {
  products: string[];
  decision: EAssessmentDecision;
  reviewingStage?: EReviewingStage;
  exclusionReason?: EExclusionReason;
  comment?: string;
};

export type TLiterature = {
  _id: string;
  archived: boolean;
  organization: string;
  stats: TStats;
  draft?: TLiteratureDraft;
  versions: TLiteratureVersion[];
  assessments: TLiteratureAssessment[];
};

export const isTLiteratureVersion = (
  value: TLiteratureDraft | TLiteratureVersion | undefined
): value is TLiteratureVersion => !!value && 'id' in value;

export const isTLiteratureDraft = (
  value: TLiteratureDraft | TLiteratureVersion | undefined
): value is TLiteratureDraft => !!value && !('id' in value);

// Backend returns { name } only - narrower than TResolvedItemsMap.
export type TLiteratureResolvedItems = Record<string, { name: string }>;

export type TAssessmentCounts = {
  included: number;
  excluded: number;
  pending: number;
};

export type TUseLiteratureResult = {
  literature: TLiterature & { resolvedPath: TSparseItem[] };
  permissions: TPermissions;
  resolvedItems: TLiteratureResolvedItems;
};

export type TLiteratureListResult = {
  items: TLiterature[];
  total: number;
  assessmentCounts?: TAssessmentCounts;
};

export enum EBulkLiteraturesImportItemStatus {
  CREATED = 'CREATED',
  FAILED_NO_IDENTIFIER = 'FAILED_NO_IDENTIFIER',
  FAILED_LOOKUP = 'FAILED_LOOKUP',
  DUPLICATE = 'DUPLICATE',
  FAILED_OTHER = 'FAILED_OTHER',
}

export type TBulkLiteraturesImportResult = {
  originalname?: string; // PDF rows only
  row?: number; // CSV line number (header is line 1), CSV rows only
  pmid?: string;
  status: EBulkLiteraturesImportItemStatus;
  literatureId?: string;
  doi?: string;
  failureReason?: string;
  // Populated on DUPLICATE when the bulk-import assessment covered products the existing literature
  // wasn't yet assessed for. Presence means "a new assessment for these products was added to the
  // existing record"; absence means the existing record was untouched.
  addedAssessmentProductIds?: string[];
};

export type TBulkLiteraturesImport = TBulkOperation & {
  results: TBulkLiteraturesImportResult[];
};

export type TSaveLiteratureResult = {
  draft: TLiteratureDraft | undefined;
  stats: TStats;
};

export type TCompleteLiteratureResult = {
  version: TLiteratureVersion;
  stats: TStats;
};

export type TCreateDraftResult = {
  draft: TLiteratureDraft | undefined;
  stats: TStats;
};
